import React from 'react'
import styled from 'styled-components'
import QRCode from 'react-qr-code'
import { useTranslation } from 'react-i18next'
import { AbwaabLogoIcon } from '@Assets/Brand'
import { Icon as AbwIcon } from '@Components/AbwaabPlus'

export type StudentCardDataArgs = {
	userInfo?: {
		phone?: string
		first_name?: string
		last_name?: string
		username?: string
		profile_photo?: string
	}
	educationalInfo?: {
		diploma?: string
		language?: string
		year?: string
		section?: string
	}
}

type Props = { data: StudentCardDataArgs }

const EMPTY = '—'

/**
 * Shared AbwaabPlus student ID card — the original eduact card's compact
 * landscape layout (avatar + name/username/phone, then the four educational
 * fields + QR), restyled on-brand (navy gradient, white abwaab mark). Used in
 * the signup complete-profile step and the dashboard profile panel. The avatar
 * is a profile-picture slot: real photo when present, else initials, else a
 * user glyph. The QR encodes the student username. Display only; no upload.
 */
const StudentIdCard: React.FC<Props> = ({ data }) => {
	const { t } = useTranslation('inputs')
	const { userInfo, educationalInfo } = data
	const fullName = [userInfo?.first_name, userInfo?.last_name].filter(Boolean).join(' ')
	const initials = `${userInfo?.first_name?.[0] ?? ''}${userInfo?.last_name?.[0] ?? ''}`.trim().toUpperCase()
	const qrValue = userInfo?.username || userInfo?.phone || 'abwaab-student'

	const eduRows: Array<[string, string]> = [
		[t('diploma'), educationalInfo?.diploma || EMPTY],
		[t('academic_year'), educationalInfo?.year || EMPTY],
		[t('academic_lang'), educationalInfo?.language || EMPTY],
		[t('educational_division'), educationalInfo?.section || EMPTY],
	]

	return (
		<Card>
			<Head>
				<Title>{t('student_id')}</Title>
				<Brand src={AbwaabLogoIcon} alt={t('abwaab', { ns: 'generic' })} />
			</Head>

			<Body>
				<PhotoSlot aria-label="profile photo">
					{userInfo?.profile_photo ? (
						<img src={userInfo.profile_photo} alt="" />
					) : initials ? (
						<span className="initials">{initials}</span>
					) : (
						<AbwIcon name="user" size={26} color="rgba(255,255,255,0.85)" />
					)}
					{!userInfo?.profile_photo && (
						<span className="hint">
							<AbwIcon name="image" size={11} color="#fff" />
						</span>
					)}
				</PhotoSlot>
				<Info>
					<Name>{fullName || EMPTY}</Name>
					<ContactRow>
						<AbwIcon name="user" size={13} color="rgba(255,255,255,0.7)" />
						<span dir="ltr">{userInfo?.username || EMPTY}</span>
					</ContactRow>
					<ContactRow>
						<AbwIcon name="phone" size={13} color="rgba(255,255,255,0.7)" />
						<span dir="ltr">{userInfo?.phone || EMPTY}</span>
					</ContactRow>
				</Info>
			</Body>

			<Footer>
				<EduGrid>
					{eduRows.map(([label, value]) => (
						<EduCell key={label}>
							<span className="k">{label}</span>
							<span className="v">{value}</span>
						</EduCell>
					))}
				</EduGrid>
				<QrBox>
					<QRCode value={qrValue} size={56} />
				</QrBox>
			</Footer>
		</Card>
	)
}

export default StudentIdCard

const Card = styled.div`
	width: 100%;
	max-width: 400px;
	border-radius: var(--r-xl);
	background: var(--gradient-course-header);
	color: #fff;
	padding: 18px;
	box-shadow: var(--sh-overlay);
`
const Head = styled.div`
	display: flex;
	align-items: center;
	justify-content: space-between;
	margin-bottom: 14px;
`
const Title = styled.span`
	font: var(--fw-bold) 15px var(--font-sans);
	color: rgba(255, 255, 255, 0.92);
`
const Brand = styled.img`
	height: 24px;
	width: auto;
	filter: brightness(0) invert(1);
`
const Body = styled.div`
	display: flex;
	align-items: center;
	gap: 14px;
	padding-bottom: 14px;
	border-bottom: 1px solid rgba(255, 255, 255, 0.18);
`
const PhotoSlot = styled.div`
	position: relative;
	flex: none;
	width: 72px;
	height: 72px;
	border-radius: var(--r-lg);
	background: rgba(255, 255, 255, 0.12);
	border: 1.5px solid rgba(255, 255, 255, 0.45);
	display: flex;
	align-items: center;
	justify-content: center;
	overflow: visible;
	img {
		width: 100%;
		height: 100%;
		object-fit: cover;
		border-radius: var(--r-lg);
	}
	.initials {
		font: var(--fw-bold) 24px var(--font-sans);
		color: #fff;
		letter-spacing: 1px;
	}
	.hint {
		position: absolute;
		bottom: -5px;
		inset-inline-end: -5px;
		width: 20px;
		height: 20px;
		border-radius: 50%;
		background: var(--blue);
		border: 2px solid #fff;
		display: flex;
		align-items: center;
		justify-content: center;
	}
`
const Info = styled.div`
	flex: 1;
	min-width: 0;
	display: flex;
	flex-direction: column;
	gap: 3px;
`
const Name = styled.div`
	font: var(--fw-bold) 16px var(--font-sans);
	white-space: nowrap;
	overflow: hidden;
	text-overflow: ellipsis;
`
const ContactRow = styled.div`
	display: flex;
	align-items: center;
	gap: 6px;
	margin-top: 2px;
	font: var(--fw-regular) 12px var(--font-sans);
	color: rgba(255, 255, 255, 0.85);
	svg {
		flex: none;
	}
	span {
		white-space: nowrap;
		overflow: hidden;
		text-overflow: ellipsis;
	}
`
const Footer = styled.div`
	display: flex;
	align-items: flex-start;
	justify-content: space-between;
	gap: 14px;
	padding-top: 14px;
`
const EduGrid = styled.div`
	flex: 1;
	display: grid;
	grid-template-columns: 1fr 1fr;
	gap: 10px 12px;
	min-width: 0;
`
const EduCell = styled.div`
	display: flex;
	flex-direction: column;
	gap: 2px;
	min-width: 0;
	.k {
		font: var(--fw-regular) 11px var(--font-sans);
		color: rgba(255, 255, 255, 0.65);
	}
	.v {
		font: var(--fw-medium) 13px var(--font-sans);
		color: #fff;
		white-space: nowrap;
		overflow: hidden;
		text-overflow: ellipsis;
	}
`
const QrBox = styled.div`
	flex: none;
	background: #fff;
	padding: 5px;
	border-radius: var(--r-sm);
	line-height: 0;
`
